import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {Disablable, IDisablable} from '@mintdata/mint-kit/api/models/mixins/Disablable';
import {ISelectable, Selectable} from '@mintdata/mint-kit/api/models/mixins/Selectable';
import {IDisposable} from '@mintdata/mint-kit/api/resources/IDisposable';
import {AnEventListener, EventBroadcaster} from '@mintdata/mint-kit/impl/events/Events';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _remove from 'lodash/remove';

export type DataSource<T> = Array<T> | IList<T>;

export interface IListItemState
{
    id?:GUID;
    isSelected:boolean;
    isDisabled:boolean;
}

export interface IListItem<T> extends ISelectable, IDisablable
{
    getUid:() => GUID;
    getData:() => T;
    getState:() => IListItemState;
    isEqual:(item:IListItem<T>) => boolean;
}

function Mixin(...mixins:any[])
{
    return function(base:any)
    {
        mixins.forEach(mixin =>
        {
            Object.getOwnPropertyNames(mixin.prototype).forEach(name =>
            {
                base.prototype[name] = mixin.prototype[name];
            });
        });

    };
}

@Mixin(Selectable, Disablable)
export class ListItem<T> implements IListItem<T>
{
    private readonly _uid:GUID;
    private _data:T;

    public constructor(id:GUID, data:T)
    {
        this._uid = id ? id : IdUtil.newComponentId();
        this._data = data;
    }

    public getUid():GUID
    {
        return this._uid;
    }

    public isEqual(item:IListItem<T>):boolean
    {
        return item instanceof ListItem
            && (this.getUid() === item.getUid());
    }

    public getData():T
    {
        return this._data;
    }

    public getState():IListItemState
    {
        return {
            id: this.getUid(),
            isSelected: this.isSelected(),
            isDisabled: this.isDisabled()
        };
    }

    // ISelectable
    select:() => ISelectable;
    unselect:() => ISelectable;
    toggleSelection:() => ISelectable;
    isSelected:() => boolean;

    // IDisablable
    disable:() => IDisablable;
    enable:() => IDisablable;
    toggleDisabled:() => IDisablable;
    isDisabled:() => boolean;
}

export enum ListEventType
{
    ItemsSelected = 0,
    DataSourceChanged = 1,
    ItemsAdded = 2,
    ItemsRemoved = 3,
}

export class ListEvent<T>
{
    public constructor(public readonly type:ListEventType,
                       public readonly items:Array<IListItem<T>>)
    {
    }

    public static itemsSelected<T = any>(selectedItems:Array<IListItem<T>>):ListEvent<T>
    {
        return new ListEvent(ListEventType.ItemsSelected, selectedItems);
    }

    public static dataSourceChanged<T = any>(newItems:Array<IListItem<T>>):ListEvent<T>
    {
        return new ListEvent(ListEventType.DataSourceChanged, newItems);
    }

    public static itemsAdded<T = any>(addedItems:Array<IListItem<T>>):ListEvent<T>
    {
        return new ListEvent(ListEventType.ItemsAdded, addedItems);
    }

    public static itemsRemoved<T = any>(removedItems:Array<IListItem<T>>):ListEvent<T>
    {
        return new ListEvent(ListEventType.ItemsRemoved, removedItems);
    }
}

export type ListListener<T> = AnEventListener<ListEvent<T>>;

export interface IList<T> // TODO: Implement Add and Insert operations
{
    onListEvent:(listener:ListListener<T>) => IDisposable;
    getItem:(uid:GUID) => IListItem<T>;
    getItemIndex:(uid:GUID | IListItem<T>) => number;
    getItems:() => Array<IListItem<T>>,
    getSize:() => number;
    getSelectedItems:() => Array<IListItem<T>>;
    getFirstSelectedItem:() => IListItem<T>;
    getLastSelectedItem:() => IListItem<T>;
    selectByValue:(value:T | Array<T>) => IList<T>;
    selectByValueProperty:(value:any | Array<any>, path:string) => IList<T>;
    selectById:(value:GUID | Array<GUID>) => IList<T>;
    selectPreviousItem:(isWrapAround?:boolean) => IList<T>;
    selectNextItem:(isWrapAround?:boolean) => IList<T>;
    unselectAll:(isFireEvent?:boolean) => IList<T>;
    setItems:(items:Array<T>) => IList<T>;
    removeItem:(itemUid:GUID) => IList<T>;
}

export class ListModel<T> implements IList<T>
{
    private _items:Array<IListItem<T>>;
    private _listEvent = new EventBroadcaster<ListEvent<T>>();

    public constructor(items?:Array<T>)
    {
        this.setItems(items);
    }

    public get isEmpty():boolean
    {
        return !(this._items && this._items.length > 0);
    }

    public onListEvent(listener:ListListener<T>):IDisposable
    {
        return this._listEvent.addListener(listener);
    }

    public getItem(uid:GUID):IListItem<T>
    {
        const items:Array<IListItem<T>> = this._items.filter((item:IListItem<T>) => (uid === item.getUid()));
        return items && items.length > 0 ? items[0] : null;
    }

    public getItemIndex(uidOrItem:GUID | IListItem<T>):number
    {
        const item:IListItem<T> = uidOrItem instanceof ListItem ? uidOrItem : this.getItem(uidOrItem as GUID);

        if( item )
            return this._items.indexOf(item);

        return -1;
    }

    public getItems():Array<IListItem<T>>
    {
        return this._items;
    }

    public getSize():number
    {
        return this._items != null ? this._items.length : 0;
    }

    public selectByValue(value:T | Array<T>):IList<T>
    {
        const valuesToSelect:Array<T> = _isArray(value) ? value as T[] : [value];

        this._items.forEach((item:IListItem<T>) =>
        {
            const itemData = item.getData();

            if( valuesToSelect.includes(itemData) )
                item.select();
        });

        this._listEvent.fireEvent(ListEvent.itemsSelected(this.getSelectedItems()));

        return this;
    }

    public selectByValueProperty(value:any | Array<any>, path:string):IList<T>
    {
        const valuesToSelect:Array<Partial<T>> = _isArray(value) ? value : [value];

        this._items.forEach((item:IListItem<T>) =>
        {
            const itemData = item.getData();

            if( valuesToSelect.includes(_get(itemData, path)) )
                item.select();
        });

        this._listEvent.fireEvent(ListEvent.itemsSelected(this.getSelectedItems()));

        return this;
    }

    public selectById(id:GUID | Array<GUID>):IList<T>
    {
        const idsToSelect:Array<GUID> = _isArray(id) ? id as GUID[] : [id];
        this._items.forEach((item:IListItem<T>) =>
        {
            if( idsToSelect.includes(item.getUid()) )
                item.select();
        });

        this._listEvent.fireEvent(ListEvent.itemsSelected(this.getSelectedItems()));
        return this;
    }

    public getSelectedItems():Array<IListItem<T>>
    {
        return this._items.filter((item:IListItem<T>) => item.isSelected());
    }

    public getFirstSelectedItem():IListItem<T>
    {
        const selectedItems:Array<IListItem<T>> = this.getSelectedItems();
        return selectedItems && selectedItems.length > 0 ? selectedItems[0] : null;
    }

    public getLastSelectedItem():IListItem<T>
    {
        const selectedItems:Array<IListItem<T>> = this.getSelectedItems();
        return selectedItems && selectedItems.length > 0 ? selectedItems[selectedItems.length - 1] : null;
    }

    public selectFirstItem():IList<T>
    {
        if( this.getSize() > 0 )
            this._items[0].select();

        return this;
    }

    public selectPreviousItem(isWrapAround:boolean = true):IList<T>
    {
        if( this._items && this._items.length > 0 )
        {
            const firstSelectedItem:IListItem<T> = this.getFirstSelectedItem();
            const firstSelectedItemIndex:number = this.getItemIndex(firstSelectedItem);

            this.unselectAll(false);

            this._items[
                firstSelectedItemIndex > 0
                    ? firstSelectedItemIndex - 1
                    : isWrapAround
                    ? this._items.length - 1
                    : 0
                ].select();
        }

        this._listEvent.fireEvent(ListEvent.itemsSelected(this.getSelectedItems()));

        return this;
    }

    public selectNextItem(isWrapAround:boolean = true):IList<T>
    {
        if( this._items && this._items.length > 0 )
        {
            const lastSelectedItem:IListItem<T> = this.getLastSelectedItem();
            const lastSelectedItemIndex:number = this.getItemIndex(lastSelectedItem);

            this.unselectAll(false);

            this._items[
                lastSelectedItemIndex < this._items.length - 1
                    ? lastSelectedItemIndex + 1
                    : !isWrapAround
                    ? this._items.length - 1
                    : 0
                ].select();
        }

        this._listEvent.fireEvent(ListEvent.itemsSelected(this.getSelectedItems()));

        return this;
    }

    public unselectAll(isFireEvent:boolean = true):IList<T>
    {
        this._items.forEach((item:IListItem<T>) => item.unselect());

        if( isFireEvent )
            this._listEvent.fireEvent(ListEvent.itemsSelected(this.getSelectedItems()));

        return this;
    }

    public setItems(items:Array<T> = []):IList<T>
    {
        this._items = items ? items.map((item:T) =>
        {
            const itemState:IListItemState = item as any;
            const listItem:ListItem<T> = new ListItem(itemState.id, item);

            if( itemState.isSelected ) // TODO: Think about default selection mechanism or selection change
                listItem.select();

            if( itemState.isDisabled ) // -//-
                listItem.disable();

            return listItem;
        }) : [];

        this._listEvent.fireEvent(ListEvent.dataSourceChanged(this._items));

        return this;
    }

    public removeItem(itemUid:GUID):IList<T>
    {
        const removedItems:Array<IListItem<T>> = _remove(this._items, (item:IListItem<T>) => (item.getUid() === itemUid));

        this._listEvent.fireEvent(ListEvent.itemsRemoved(removedItems));

        return this;
    }
}

